/**
 * Horse Management Page
 * 
 * Horse ownership is at the USER level:
 * - For managers: "Club Horses" are stored at the manager's user level and shared across all clubs they manage
 * - For players: "Club Horses" shows the manager's horses (read-only), "My Horses" shows their own horses
 */
import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Layout from "../components/layout/Layout";
import HorseList from "../components/HorseManagement/HorseList";
import ClubPickerPrompt from "../components/clubs/ClubPickerPrompt";
import { Horse, Player } from "../components/HorseManagement/types";

interface HorseManagementPageProps {
  clubId: number;
  devPort: string;
  waiverNotifications: number;
  managerMode: number;
  userName?: string;
}

function HorseManagementPage({
  clubId,
  devPort,
  waiverNotifications,
  managerMode,
  userName,
}: HorseManagementPageProps) {
  const [managerHorses, setManagerHorses] = useState<Horse[]>([]);
  const [myHorses, setMyHorses] = useState<Horse[]>([]);
  const [playerHorsesMap, setPlayerHorsesMap] = useState<Record<string, Horse[]>>({});
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [clubManagerId, setClubManagerId] = useState<string | null>(null);

  const isManager = managerMode === -1;
  const hasClub = !!clubId && clubId > 0;

  // Fetch manager's user ID if in manager mode
  useEffect(() => {
    if (isManager && userName) {
      fetch(devPort + "system_get_manager/" + userName, {
        credentials: "include",
      })
        .then((response) => response.json())
        .then((data) => {
          if (data && data[0] && data[0][0]) {
            setUserId(String(data[0][0]));
          }
        })
        .catch(() => {});
    } else if (!isManager && managerMode) {
      setUserId(String(managerMode));
    }
  }, [devPort, userName, isManager, managerMode]);

  // For players: fetch the club's manager ID to show their horses
  useEffect(() => {
    if (!isManager && !hasClub) {
      setClubManagerId(null);
      setManagerHorses([]);
    }
    if (!isManager && hasClub) {
      fetch(devPort + "get_club_manager/" + clubId)
        .then((response) => response.json())
        .then((data) => {
          if (data && data[0] && data[0][0]) {
            setClubManagerId(String(data[0][0]));
          }
        })
        .catch(() => {});
    }
  }, [devPort, clubId, isManager, hasClub]);

  // Load manager's horses (for managers: their own, for players: the club manager's)
  const loadManagerHorses = useCallback(() => {
    const managerId = isManager ? userId : clubManagerId;
    if (!managerId) return Promise.resolve();
    
    return fetch(devPort + "horses/user/" + managerId, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setManagerHorses(data);
        } else {
          setManagerHorses([]);
        }
      })
      .catch(() => {
        setManagerHorses([]);
      });
  }, [devPort, userId, clubManagerId, isManager]);

  // Load player's own horses (only for players)
  const loadMyHorses = useCallback(() => {
    if (isManager || !userId) return Promise.resolve();
    
    return fetch(devPort + "horses/user/" + userId, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setMyHorses(data);
        } else {
          setMyHorses([]);
        }
      })
      .catch(() => {
        setMyHorses([]);
      });
  }, [devPort, userId, isManager]);

  // Load players for the club
  const loadPlayers = useCallback(() => {
    if (!hasClub) {
      setPlayers([]);
      return Promise.resolve([]);
    }
    return fetch(devPort + "player_list/" + clubId + "/False")
      .then((response) => response.json())
      .then((data) => {
        if (data) {
          const formattedPlayers = data.map((player: any[]) => ({
            player_id: player[4],
            first_name: player[0],
            last_name: player[1],
          }));
          setPlayers(formattedPlayers);
          return formattedPlayers;
        }
        return [];
      })
      .catch(() => []);
  }, [devPort, clubId, hasClub]);

  // Load horses for all players in the club (for manager view) — single API call
  const loadPlayerHorses = useCallback(async () => {
    if (!isManager) return;

    try {
      const response = await fetch(devPort + "horses/club-players/" + clubId);
      const data = await response.json();
      if (data && typeof data === "object" && !Array.isArray(data)) {
        setPlayerHorsesMap(data);
      } else {
        setPlayerHorsesMap({});
      }
    } catch {
      setPlayerHorsesMap({});
    }
  }, [devPort, isManager, clubId]);

  // Load all data
  const loadAllData = useCallback(async () => {
    setLoading(true);
    
    await Promise.all([
      loadManagerHorses(),
      loadMyHorses(),
      loadPlayers(),
      loadPlayerHorses(),
    ]);
    
    setLoading(false);
  }, [loadManagerHorses, loadMyHorses, loadPlayers, loadPlayerHorses]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // For managers: get players who have horses
  const playersWithHorses = players.filter((p) => 
    playerHorsesMap[String(p.player_id)]?.length > 0
  );

  // Get player horses from the map
  const getPlayerHorses = (playerId: string | number) => {
    return playerHorsesMap[String(playerId)] || [];
  };

  // Managers: their own horses shared across clubs. Players: read-only view of the club manager's horses.
  const clubHorsesSection = (
    <Paper elevation={24}>
      <Card variant="elevation">
        <Accordion
          defaultExpanded
          sx={{
            boxShadow: "none",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: "100%" }}>
              <Stack sx={{ flex: 1 }}>
                <Typography variant="h6">Club Horses</Typography>
                {isManager && (
                  <Typography variant="caption" color="text.secondary">
                    Shared across all your clubs
                  </Typography>
                )}
              </Stack>
              <Chip label={managerHorses.length} size="small" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <HorseList
              devPort={devPort}
              clubId={clubId}
              managerMode={managerMode}
              ownerFilter="player"
              ownerId={isManager ? userId || undefined : clubManagerId || undefined}
              showFilters={isManager}
              canAdd={isManager}
              canEdit={isManager}
              canDelete={isManager}
              onHorsesChange={loadManagerHorses}
              horses={managerHorses}
              players={players}
              loading={loading}
              isUserLevel={true}
              emptyMessage={isManager
                ? "No horses yet. Click 'Add Horse' to add horses. These horses will be available across all clubs you manage."
                : "The club hasn't added any horses yet."
              }
            />
          </AccordionDetails>
        </Accordion>
      </Card>
    </Paper>
  );

  return (
    <Layout
      managerMode={managerMode}
      waiverNotifications={waiverNotifications}
      devPort={devPort}
    >
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="h6">Horse Management</Typography>

            {isManager && clubHorsesSection}

            {/* My Horses Section - only for players */}
            {!isManager && userId && (
              <Paper elevation={24}>
                <Card variant="elevation">
                  <Accordion
                    defaultExpanded
                    sx={{
                      boxShadow: "none",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ width: "100%" }}>
                        <Stack sx={{ flex: 1 }}>
                          <Typography variant="h6">My Horses</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Your personal horses (usable across all clubs)
                          </Typography>
                        </Stack>
                        <Chip label={myHorses.length} size="small" />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <HorseList
                        devPort={devPort}
                        clubId={clubId}
                        managerMode={managerMode}
                        ownerFilter="player"
                        ownerId={userId}
                        showFilters={false}
                        canAdd={true}
                        canEdit={true}
                        canDelete={true}
                        onHorsesChange={loadMyHorses}
                        horses={myHorses}
                        players={players}
                        loading={loading}
                        emptyMessage="You haven't added any horses yet. Click 'Add Horse' to add your own horses. Your horses can be used across all clubs you play at."
                        isUserLevel={true}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Card>
              </Paper>
            )}

            {!isManager && (hasClub ? clubHorsesSection : (
              <Paper elevation={24}>
                <Card variant="elevation">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Club Horses</Typography>
                    <ClubPickerPrompt
                      devPort={devPort}
                      message="Choose a club to see the horses it provides."
                    />
                  </CardContent>
                </Card>
              </Paper>
            ))}

            {/* For Managers: Player Horses Sections */}
            {isManager && playersWithHorses.length > 0 && (
              <Stack spacing={2}>
                {playersWithHorses.map((player) => {
                  const playerHorses = getPlayerHorses(player.player_id);
                  return (
                    <Paper elevation={24} key={player.player_id}>
                      <Card variant="elevation">
                        <Accordion
                          sx={{
                            boxShadow: "none",
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: "100%" }}>
                              <Typography sx={{ flex: 1 }}>
                                {player.first_name} {player.last_name}
                              </Typography>
                              <Chip
                                label={`${playerHorses.length} horse${playerHorses.length !== 1 ? "s" : ""}`}
                                size="small"
                              />
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails>
                            <HorseList
                              devPort={devPort}
                              clubId={clubId}
                              managerMode={managerMode}
                              ownerFilter="player"
                              ownerId={player.player_id}
                              showFilters={false}
                              canAdd={false}
                              canEdit={true}
                              canDelete={true}
                              onHorsesChange={loadPlayerHorses}
                              horses={playerHorses}
                              players={players}
                              loading={loading}
                              isUserLevel={true}
                            />
                          </AccordionDetails>
                        </Accordion>
                      </Card>
                    </Paper>
                  );
                })}
              </Stack>
            )}

            {/* For Managers: Show message if no player horses */}
            {isManager && playersWithHorses.length === 0 && managerHorses.length > 0 && (
              <Typography color="text.secondary">
                No players have added their own horses yet.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Layout>
  );
}

export default HorseManagementPage;
